import { useEffect, useRef, useState } from 'react';

/**
 * Animace ve vyloze.
 *
 * Dve pravidla, ktera plati bez vyjimky:
 *
 *   1. `prefers-reduced-motion` je silnejsi nez nastaveni obchodu. Obsluha
 *      si smi zvolit, jak vyrazny pohyb chce, ale ne to, ze ho uvidi i
 *      clovek, kteremu z nej je spatne.
 *   2. Zadny obsah nesmi na animaci zaviset. Vsechno se anumuje z viditelneho
 *      do viditelneho – kdyz JS nedobehne nebo IntersectionObserver chybi,
 *      stranka je proste hned cela videt.
 */

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Pojistka pro pripad, ze pozorovatel nikdy nepromluvi.
 *
 * Prohlizec ho v neviditelne zalozce neobslouzi – stranka otevrena na
 * pozadi (prostredni tlacitko, obnovena relace) by pak zustala prazdna,
 * dokud na ni clovek neprepne. Obsah schovany za animaci uz neni animace,
 * ale chyba, takze po teto dobe se ukaze tak jako tak.
 */
const REVEAL_FALLBACK_MS = 900;

/**
 * Odhaleni prvku, jakmile dojede do zorneho pole.
 *
 * Vraci ref a priznak. Trida `is-in` se pridava jednou a uz se nesundava:
 * prvek, ktery se pri kazdem odscrollovani znovu rozsviti, pusobi jako
 * chyba, ne jako efekt.
 */
export function useReveal<T extends HTMLElement>(options: { delayMs?: number } = {}) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(
    // Neviditelna zalozka nema co animovat. Az na ni clovek prepne, ma
    // obsah proste byt – ne teprve nabihat.
    () => prefersReducedMotion() || (typeof document !== 'undefined' && document.hidden),
  );

  useEffect(() => {
    if (shown) return undefined;
    const node = ref.current;
    if (node === null) return undefined;

    const timer = window.setTimeout(() => setShown(true), REVEAL_FALLBACK_MS);
    const finish = () => {
      window.clearTimeout(timer);
      setShown(true);
    };

    // Starsi prohlizec bez observeru dostane obsah rovnou.
    if (typeof IntersectionObserver === 'undefined') {
      finish();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer.disconnect();
          window.clearTimeout(timer);
          if (options.delayMs === undefined) setShown(true);
          else window.setTimeout(() => setShown(true), options.delayMs);
        }
      },
      // Spousti se driv, nez prvek dojede uplne – jinak animace zacne az
      // ve chvili, kdy uz na nej uzivatel kouka.
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [shown, options.delayMs]);

  return { ref, shown, className: shown ? 'reveal is-in' : 'reveal' };
}

/**
 * Kratky puls pri zmene hodnoty – pouziva ho pocitadlo v kosiku.
 *
 * Prvni vykresleni se zamerne nepulsuje: cislo, ktere skoci hned po nacteni
 * stranky, vypada jako chyba vykreslovani.
 */
export function usePulse(value: number): boolean {
  const [pulsing, setPulsing] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return undefined;
    previous.current = value;
    if (prefersReducedMotion()) return undefined;

    setPulsing(true);
    const timer = window.setTimeout(() => setPulsing(false), 420);
    return () => window.clearTimeout(timer);
  }, [value]);

  return pulsing;
}
